import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import useAuthStore from '../../store/authStore';
import { Clock, Calendar, PlayCircle, CalendarClock, BookOpen, FileText, Download } from 'lucide-react';
import { Badge, Button, Card, Modal, ConfirmDialog, LoadingState, EmptyState } from '../../components/ui';

const getEffectiveStatus = (exam) => {
  if (exam.status === 'ACTIVE') {
    const now = new Date();
    const start = new Date(exam.startTime);
    if (now < start) return 'UPCOMING';
  }
  return exam.status;
};

const STATUS_LABEL_KEY = {
  ACTIVE: 'ACTIVE',
  UPCOMING: 'UPCOMING',
  SCHEDULED: 'UPCOMING',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  DRAFT: 'UPCOMING',
};

const STATUS_VARIANT_MAP = {
  ACTIVE: 'success',
  UPCOMING: 'info',
  SCHEDULED: 'info',
  COMPLETED: 'neutral',
  CANCELLED: 'danger',
  DRAFT: 'info',
};

const StatusBadge = ({ exam }) => {
  const { t } = useTranslation();
  const effective = getEffectiveStatus(exam);
  const labelKey = STATUS_LABEL_KEY[effective];
  return (
    <Badge variant={STATUS_VARIANT_MAP[effective] || 'neutral'}>
      {labelKey ? t(`dashboard.statusLabels.${labelKey}`) : effective}
    </Badge>
  );
};

const PRE_ENTRY_MINUTES = 15;

// Shared card for both real exams and practice (mock) tests — previously the
// Practice Tests section re-implemented this markup inline instead of reusing it.
const ExamCard = ({ exam, attempt, isMock, onJoin, onReschedule, onDownloadScoreCard }) => {
  const { t } = useTranslation();
  const isCompleted = attempt?.status === 'COMPLETED' || attempt?.status === 'SUBMITTED';
  const now = new Date();
  const start = new Date(exam.startTime);
  const lobbyOpen = new Date(start.getTime() - PRE_ENTRY_MINUTES * 60 * 1000);
  const inPreEntryWindow = !isMock && now >= lobbyOpen && now < start && exam.status === 'ACTIVE';

  return (
    <Card className="hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-slate-900 truncate">{exam.name}</h3>
          {isMock && exam.description && (
            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{exam.description}</p>
          )}
        </div>
        {isMock ? <Badge variant="brand">{t('dashboard.card.practice')}</Badge> : <StatusBadge exam={exam} />}
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Clock className="w-4 h-4 text-slate-400 flex-shrink-0" />
          {t('dashboard.card.durationMinutes', { count: exam.duration })}
        </div>
        {!isMock && exam.startTime && (
          <div className="col-span-2 flex items-center gap-2 text-sm text-slate-600">
            <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
            {new Date(exam.startTime).toLocaleString()}
          </div>
        )}
      </div>

      {isMock && (
        <Button variant="primary" fullWidth icon={PlayCircle} onClick={() => onJoin(exam)}>
          {t('dashboard.card.startPractice')}
        </Button>
      )}

      {!isMock && exam.status === 'ACTIVE' && getEffectiveStatus(exam) === 'ACTIVE' && !isCompleted && (
        <div className="space-y-2">
          <Button variant="primary" fullWidth icon={PlayCircle} onClick={() => onJoin(exam)}>
            {t('dashboard.card.startExam')}
          </Button>
          <Button variant="outline" fullWidth icon={CalendarClock} onClick={() => onReschedule(exam)}>
            {t('dashboard.card.rescheduleExam')}
          </Button>
        </div>
      )}
      {!isMock && exam.status === 'ACTIVE' && getEffectiveStatus(exam) === 'ACTIVE' && isCompleted && !exam.allowRetakes && (
        <div className="space-y-2">
          <div className="w-full bg-green-50 border border-green-200 text-green-700 text-sm font-medium py-2.5 rounded-lg text-center">
            {t('dashboard.card.submitted')}
          </div>
          <Button variant="outline" fullWidth icon={Download} onClick={() => onDownloadScoreCard(exam)}>
            {t('dashboard.card.downloadScoreCard')}
          </Button>
        </div>
      )}
      {!isMock && exam.status === 'ACTIVE' && getEffectiveStatus(exam) === 'ACTIVE' && isCompleted && exam.allowRetakes && (
        <div className="space-y-2">
          <div className="w-full bg-green-50 border border-green-200 text-green-700 text-xs font-medium py-1.5 rounded-lg text-center">
            {attempt?.score !== undefined && attempt?.score !== null
              ? t('dashboard.card.submittedWithScore', { score: attempt.score })
              : t('dashboard.card.submitted')}
          </div>
          <Button variant="outline" fullWidth icon={Download} onClick={() => onDownloadScoreCard(exam)}>
            {t('dashboard.card.downloadScoreCard')}
          </Button>
          <Button variant="outline" fullWidth icon={PlayCircle} onClick={() => onJoin(exam)}>
            {t('dashboard.card.retakeExam')}
          </Button>
        </div>
      )}
      {!isMock && inPreEntryWindow && !isCompleted && (
        <Button
          fullWidth
          icon={FileText}
          onClick={() => onJoin(exam)}
          className="!bg-amber-500 hover:!bg-amber-600 text-white"
        >
          {t('dashboard.card.enterExamRoom')} <span className="text-xs opacity-80">{t('dashboard.card.setupSuffix')}</span>
        </Button>
      )}
      {!isMock && !inPreEntryWindow && (exam.status === 'SCHEDULED' || exam.status === 'DRAFT' || getEffectiveStatus(exam) === 'UPCOMING') && (
        <div className="space-y-2">
          <div className="w-full bg-blue-50 border border-blue-200 text-blue-700 text-sm font-medium py-2.5 rounded-lg text-center">
            {t('dashboard.card.notStartedYet')}
          </div>
          <Button variant="outline" fullWidth icon={CalendarClock} onClick={() => onReschedule(exam)}>
            {t('dashboard.card.rescheduleExam')}
          </Button>
        </div>
      )}
      {!isMock && exam.status === 'COMPLETED' && !isCompleted && (
        <div className="w-full bg-slate-50 border border-slate-200 text-slate-500 text-sm font-medium py-2.5 rounded-lg text-center">
          {t('dashboard.card.examEnded')}
        </div>
      )}
    </Card>
  );
};

const StudentDashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [mockTests, setMockTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attempts, setAttempts] = useState({});
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [pendingSlot, setPendingSlot] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [examsRes, mockRes] = await Promise.all([
          api.get('/exams/my-exams'),
          api.get('/mock-tests'),
        ]);
        const examsList = examsRes.data.data || [];
        setExams(examsList);
        const attMap = {};
        examsList.forEach(e => {
          if (e.attempt) attMap[e._id] = e.attempt;
        });
        setAttempts(attMap);
        setMockTests(mockRes.data.data || []);
      } catch (err) {
        toast.error(t('dashboard.toasts.loadExamsFailed'));
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const openReschedule = async (exam) => {
    setSelectedExam(exam);
    setShowRescheduleModal(true);
    setRescheduleLoading(true);
    try {
      const res = await api.get(`/exams/reschedule-slots/${exam._id}`);
      setAvailableSlots(res.data.data || []);
    } catch (err) {
      toast.error(t('dashboard.toasts.loadSlotsFailed'));
    } finally {
      setRescheduleLoading(false);
    }
  };

  const handleReschedule = async (newExamId) => {
    try {
      await api.post(`/exams/${selectedExam._id}/reschedule`, { newExamId });
      toast.success(t('dashboard.toasts.rescheduleSuccess'));
      setShowRescheduleModal(false);
      setPendingSlot(null);
      const examsRes = await api.get('/exams/my-exams');
      setExams(examsRes.data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || t('dashboard.toasts.rescheduleFailed'));
    }
  };

  const handleJoin = (exam) => {
    const now = new Date();
    const start = new Date(exam.startTime);
    const end = new Date(exam.endTime);
    const lobbyOpen = new Date(start.getTime() - PRE_ENTRY_MINUTES * 60 * 1000);

    if (now < lobbyOpen) {
      toast.error(t('dashboard.toasts.lobbyNotOpen', { time: lobbyOpen.toLocaleString(), minutes: PRE_ENTRY_MINUTES }));
      return;
    }
    if (now > end) {
      toast.error(t('dashboard.toasts.examAlreadyEnded'));
      return;
    }

    navigate(`/student/exam/${exam._id || exam.id}`);
  };

  const handleDownloadScoreCard = async (exam) => {
    try {
      const res = await api.get(`/results/my/${exam._id || exam.id}/scorecard`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Score_Card_${exam.name.replace(/[^a-z0-9]/gi, '_')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(t('dashboard.toasts.scoreCardFailed'));
    }
  };

  const handleJoinMock = (mock) => navigate(`/student/mock/${mock._id}`);

  const upcoming = exams.filter(e => e.status === 'SCHEDULED' || e.status === 'DRAFT' || getEffectiveStatus(e) === 'UPCOMING');
  const active = exams.filter(e => e.status === 'ACTIVE' && getEffectiveStatus(e) === 'ACTIVE');
  const past = exams.filter(e => e.status === 'COMPLETED' || e.status === 'CANCELLED');

  if (loading) {
    return <LoadingState label={t('dashboard.loadingExams')} />;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-8">
      {/* Welcome */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900">
          {t('dashboard.welcome', { name: user?.name?.split(' ')[0] || t('dashboard.defaultName') })}
        </h2>
        <p className="text-slate-500 text-sm mt-1">{t('dashboard.subtitle')}</p>
      </div>

      {/* Active Exams */}
      {active.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />
            <h3 className="text-lg font-semibold text-slate-800">{t('dashboard.sections.activeNow')}</h3>
            <Badge variant="success">{active.length}</Badge>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {active.map(e => (
              <ExamCard key={e._id || e.id} exam={e} attempt={attempts[e._id || e.id]} onJoin={handleJoin} onReschedule={openReschedule} onDownloadScoreCard={handleDownloadScoreCard} />
            ))}
          </div>
        </section>
      )}

      {/* Upcoming Exams */}
      {upcoming.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <h3 className="text-lg font-semibold text-slate-800">{t('dashboard.sections.upcomingExams')}</h3>
            <Badge variant="info">{upcoming.length}</Badge>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {upcoming.map(e => (
              <ExamCard key={e._id || e.id} exam={e} attempt={attempts[e._id || e.id]} onJoin={handleJoin} onReschedule={openReschedule} onDownloadScoreCard={handleDownloadScoreCard} />
            ))}
          </div>
        </section>
      )}

      {/* Past Exams */}
      {past.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <h3 className="text-lg font-semibold text-slate-800">{t('dashboard.sections.pastExams')}</h3>
            <Badge variant="neutral">{past.length}</Badge>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {past.map(e => (
              <ExamCard key={e._id || e.id} exam={e} attempt={attempts[e._id || e.id]} onJoin={handleJoin} onReschedule={openReschedule} onDownloadScoreCard={handleDownloadScoreCard} />
            ))}
          </div>
        </section>
      )}

      {/* Mock / Practice Tests */}
      {mockTests.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="w-5 h-5 text-themePrimary" />
            <h3 className="text-lg font-semibold text-slate-800">{t('dashboard.sections.practiceTests')}</h3>
            <Badge variant="brand">{mockTests.length}</Badge>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {mockTests.map(mock => (
              <ExamCard key={mock._id} exam={mock} isMock onJoin={handleJoinMock} />
            ))}
          </div>
        </section>
      )}

      {exams.length === 0 && mockTests.length === 0 && (
        <EmptyState
          icon={FileText}
          title={t('dashboard.emptyState.title')}
          description={t('dashboard.emptyState.description')}
        />
      )}

      {/* Reschedule Modal */}
      <Modal
        open={showRescheduleModal}
        onClose={() => setShowRescheduleModal(false)}
        title={t('dashboard.reschedule.modalTitle')}
        size="md"
      >
        <p className="text-sm text-slate-500 -mt-2 mb-4">{t('dashboard.reschedule.pickSlot', { examName: selectedExam?.name })}</p>
        <div className="max-h-[60vh] overflow-y-auto space-y-3">
          {rescheduleLoading ? (
            <LoadingState label={t('dashboard.reschedule.loadingSlots')} className="py-12" />
          ) : availableSlots.length === 0 ? (
            <EmptyState icon={CalendarClock} title={t('dashboard.reschedule.noSlotsTitle')} description={t('dashboard.reschedule.noSlotsDescription')} className="py-8" />
          ) : (
            availableSlots.map(slot => (
              <div key={slot._id} className="border border-slate-200 rounded-lg p-4 flex items-center justify-between gap-3 hover:border-themePrimary/30 transition-colors">
                <div className="min-w-0">
                  <h4 className="font-medium text-slate-900 truncate">{slot.name}</h4>
                  <p className="text-sm text-slate-500 mt-0.5">{new Date(slot.startTime).toLocaleString()}</p>
                </div>
                <Button size="sm" onClick={() => setPendingSlot(slot)}>{t('dashboard.reschedule.chooseSlot')}</Button>
              </div>
            ))
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={!!pendingSlot}
        onClose={() => setPendingSlot(null)}
        onConfirm={() => handleReschedule(pendingSlot._id)}
        title={t('dashboard.reschedule.confirmTitle')}
        description={t('dashboard.reschedule.confirmDescription')}
        confirmLabel={t('dashboard.reschedule.confirmLabel')}
        cancelLabel={t('common.cancel')}
      />
    </div>
  );
};

export default StudentDashboard;
